/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement } from 'react';
import { EachRow, ROAM_WARN_METRES } from '../../_helpers/bleEach';
import { DEFAULT_GATES, REJECTION_TEXT } from '../../_helpers/eachFit';
import { CARD_CLASS } from '../ParticleBins/particleBinShared';

type Props = {
  rows: EachRow[];
  /** Fitting window in minutes. */
  windowMin: number;
  /** Selectable fitting windows. */
  windowChoices: number[];
  onWindowChange: (minutes: number) => void;
  /** Room volume in m³, or null when not supplied. */
  volumeM3: number | null;
  onVolumeChange: (volume: number | null) => void;
  /** How far the phone moved during the window; null when no GPS. */
  roamMetres: number | null;
  /** True while the page is still buffering its first window. */
  loading: boolean;
};

/** "5 min", "6 h", "7 days" — a window length that may be minutes or weeks. */
function windowLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / 1440)} days`;
}

/** Compact engineering format — these span ppm/h to 1e10 particles/h. */
function eng(v: number | null | undefined, digits = 2): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  if (abs >= 1e5 || abs < 1e-2) return v.toExponential(1);
  if (abs >= 100) return v.toFixed(0);
  return v.toFixed(digits);
}

/** The eACH value with its 95% interval, when the window bounded one. */
function rateText(row: EachRow): string {
  if (!row.fit) return '—';
  const { lambda, lambdaLow, lambdaHigh } = row.fit;
  if (lambdaLow === null || lambdaHigh === null) return `${eng(lambda)} /h`;
  return `${eng(lambda)} /h  (${eng(lambdaLow)}–${eng(lambdaHigh)})`;
}

/**
 * Effective air changes per hour and source emission strength for CO₂, PN0.3
 * and PM2.5, from an exponential fit of the live readings.
 *
 * WHAT IS ON SCREEN AND WHY. Every row shows its R² and the number of points
 * fitted even when the value is withheld, because "no eACH right now" is the
 * normal state: an exponential only describes a room that is clearing or
 * filling, and most five-minute windows are neither. Scanning production data,
 * about 1% of five-minute windows pass the R² ≥ 0.75 gate — so a card that
 * only ever said "unavailable" with no numbers would look broken rather than
 * correct.
 *
 * The rate carries an interval, and a window that cannot resolve the rate says
 * so instead of printing a confident number: over five minutes an air change
 * rate of 1 /h moves the concentration by only 8%, which fits a straight line
 * just as well as an exponential. Longer windows are offered for that reason.
 */
export default function VentilationCard({
  rows,
  windowMin,
  windowChoices,
  onWindowChange,
  volumeM3,
  onVolumeChange,
  roamMetres,
  loading
}: Props): ReactElement {
  const moved = roamMetres !== null && roamMetres > ROAM_WARN_METRES;

  return (
    <div className={`flex flex-col gap-3 ${CARD_CLASS}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-100">
            Ventilation — effective air changes per hour
          </h3>
          <p className="text-xs text-gray-500">
            Exponential fit of each parameter over {windowLabel(windowMin)}
            {windowChoices.length === 0 ? ' (the selected range)' : ''}. Reported only at
            R² ≥ 0.75, and only when both halves of the window agree.
            {windowMin > 0 && (
              <>
                {' '}
                This window can resolve rates above about{' '}
                <strong className="text-gray-400">
                  {eng((DEFAULT_GATES.minResolution * 60) / windowMin, 1)} /h
                </strong>
                ; use a longer one for slower rooms.
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            {windowChoices.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onWindowChange(m)}
                className={`rounded border px-2 py-1 text-xs transition ${
                  windowMin === m
                    ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                    : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
                }`}
              >
                {m < 60 ? `${m} min` : `${m / 60} h`}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-400" htmlFor="ble-volume">
            Room volume
            <input
              id="ble-volume"
              type="number"
              min={0}
              step={1}
              placeholder="m³"
              value={volumeM3 ?? ''}
              onChange={(e) =>
                onVolumeChange(e.target.value === '' ? null : Number(e.target.value))
              }
              className="w-20 rounded border border-gray-800 bg-background-700 px-2 py-1 text-gray-100 focus:border-primary-500 focus:outline-none"
            />
            m³
          </label>
        </div>
      </div>

      {moved && (
        <div className="rounded border border-warning-500/40 bg-warning-500/10 px-3 py-2 text-xs text-warning-300">
          The phone moved about {roamMetres?.toFixed(0)} m during this window. An air change
          rate only describes one room — a reading that changed because the sensor was carried
          somewhere else will still fit a curve, so treat these values as unreliable until the
          sensor stays put.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3 font-medium">Parameter</th>
              <th className="py-2 pr-3 font-medium">
                eACH
                <span className="ml-1 font-normal normal-case text-gray-600">(95%)</span>
              </th>
              <th className="py-2 pr-3 font-medium">R²</th>
              <th className="py-2 pr-3 font-medium">Plateau</th>
              <th className="py-2 pr-3 font-medium">Source strength</th>
              <th className="py-2 pr-3 font-medium">Points</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const ok = row.rejection === null;
              const {emission} = row;
              return (
                <tr key={row.target.id} className="border-b border-gray-800/60 align-top">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-100">{row.target.label}</div>
                    <div className="text-xs text-gray-500">
                      {row.sourceKey ? row.sourceKey.replace(/_/g, ' ') : 'no reading'}
                      {' · bg '}
                      {eng(row.background)} {row.target.unit}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    {ok ? (
                      <span
                        className="font-semibold text-success-400"
                        title="Best fit, with the 95% interval the window supports. A wide interval means the window saw too little of the curve to pin the rate down."
                      >
                        {rateText(row)}
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-gray-300">
                    {row.fit ? row.fit.r2.toFixed(3) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-300">
                    {row.fit ? (
                      <>
                        {eng(row.fit.asymptote)} {row.target.unit}
                        {row.fit.asymptote < 0 && (
                          <span
                            className="ml-1 text-warning-400"
                            title="The fitted plateau is below zero, which is not physical — the model is being extrapolated past where it holds."
                          >
                            ⚠
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {ok && emission ? (
                      <div className="text-gray-200">
                        {eng(emission.perVolume)} {row.target.unit}/h per m³
                        {emission.absolute !== null && (
                          <div className="text-xs text-gray-400">
                            {eng(emission.absolute)} {emission.absoluteUnit} in {volumeM3} m³
                            {row.persons !== null && (
                              <> · ≈ {row.persons.toFixed(1)} people</>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-gray-400">
                    {row.fit ? `${row.fit.n} of ${row.rawCount}` : `0 of ${row.rawCount}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Why each row is withheld, spelled out — a blank cell with no reason
          reads as a broken page rather than as a gate doing its job. */}
      <div className="flex flex-col gap-1">
        {rows
          .filter((r) => r.rejection !== null)
          .map((r) => (
            <p key={r.target.id} className="text-xs text-gray-500">
              <span className="text-gray-400">{r.target.label}:</span>{' '}
              {REJECTION_TEXT[r.rejection as keyof typeof REJECTION_TEXT]}
            </p>
          ))}
        {loading && <p className="text-xs text-gray-500">Buffering the first window…</p>}
      </div>

      <p className="text-xs text-gray-600">
        CO₂ is inert, so its rate is the air change rate. PN0.3 and PM2.5 also settle onto
        surfaces and may be filtered, so their rates are an upper bound on ventilation — which
        is why they are listed separately rather than averaged. Source strength assumes the
        plateau is a steady state: S/V = eACH × (plateau − background). The bracketed range is
        the 95% interval; a wide one means the window saw too little of the curve to pin the
        rate down, and is the number to trust over the single value in front of it.
      </p>
    </div>
  );
}
